import { Matrix } from './Matrix';
import { ColorTransform } from './ColorTransform';
import { Rectangle } from './Rectangle';
import { TMatrix, TWIPS, fround, toTwips } from '../utils/numeric';

/**
 * flash.geom.Transform: a live view on a display object's matrix / colour transform.
 * Getters return copies (Flash semantics: `mc.transform.matrix.a = 2` has no effect).
 * Setting `matrix`/`colorTransform` marks the object as transformed by script.
 */
export class Transform {
  /** @internal */ $obj: any;

  constructor(displayObject?: any) {
    this.$obj = displayObject;
  }

  get matrix(): Matrix {
    const m: TMatrix = this.$obj.$m;
    return new Matrix(m.a, m.b, m.c, m.d, m.tx / TWIPS, m.ty / TWIPS);
  }
  set matrix(v: Matrix) {
    if (!v) return;
    this.$obj.$setMatrix(matrixToTwips(v));
    this.$obj.$transformedByScript = true;
  }

  get colorTransform(): ColorTransform {
    const o = this.$obj;
    return new ColorTransform(o.$ctRM / 256, o.$ctGM / 256, o.$ctBM / 256, o.$ctAM / 256, o.$ctRO, o.$ctGO, o.$ctBO, o.$ctAO);
  }
  set colorTransform(ct: ColorTransform) {
    if (!ct) throw new TypeError('Error #2007: Parameter colorTransform must be non-null.');
    this.$obj.$setColorTransformFrom(ct);
    this.$obj.$transformedByScript = true;
  }

  get concatenatedMatrix(): Matrix {
    const m: TMatrix = this.$obj.$localToGlobalMatrix();
    return new Matrix(m.a, m.b, m.c, m.d, m.tx / TWIPS, m.ty / TWIPS);
  }

  get concatenatedColorTransform(): ColorTransform {
    const out = new ColorTransform();
    const chain: any[] = [];
    let o = this.$obj;
    while (o) {
      chain.push(o);
      o = o.$parent;
    }
    for (let i = chain.length - 1; i >= 0; i--) {
      const c = chain[i];
      out.concat(new ColorTransform(c.$ctRM / 256, c.$ctGM / 256, c.$ctBM / 256, c.$ctAM / 256, c.$ctRO, c.$ctGO, c.$ctBO, c.$ctAO));
    }
    return out;
  }

  get pixelBounds(): Rectangle {
    const r = this.$obj.$worldBounds();
    if (!(r.xMin <= r.xMax && r.yMin <= r.yMax)) return new Rectangle(0, 0, 0, 0);
    const x0 = Math.floor(r.xMin / TWIPS);
    const y0 = Math.floor(r.yMin / TWIPS);
    return new Rectangle(x0, y0, Math.ceil(r.xMax / TWIPS) - x0, Math.ceil(r.yMax / TWIPS) - y0);
  }

  /** @internal */ $matrixTwips(): TMatrix {
    return { ...this.$obj.$m };
  }
}

export function matrixToTwips(v: Matrix): TMatrix {
  return { a: fround(v.a), b: fround(v.b), c: fround(v.c), d: fround(v.d), tx: toTwips(v.tx), ty: toTwips(v.ty) };
}
